// ----------------------------------------------------------------------
// <copyright file="Izenda.TenantManagementStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var fetcher = require('fetchers/Izenda.TenantManagementDataFetcher');
  var accessFetcher = require('fetchers/Izenda.AccessDataFetcher');
  var TenantDetails = require('logic/models/Izenda.TenantDetails');
  var TenantList = require('logic/models/Izenda.TenantList');
  var constants = require('logic/data/Izenda.TenantConstants');
  var CommonMessages = require('share/messages/Izenda.CommonMessages');
  var MobxUtils = require('share/utils/Izenda.MobxUtils');
  var PermissionStore = require('logic/Izenda.PermissionStore');
  var AccessStore = require('logic/Izenda.PermissionAccessStore');
  var IS_TENANT_SETUP = true;
  var localizer = require('share/localization/Izenda.Localizer');
  var uiState = require('logic/Izenda.UiState');
  const { isBoolean, map, find, findIndex, cloneDeep } = require('lodash/fp');

  function TenantManagementStore() {
    m.extendObservable(this, {
      showingTenantCloned: {},
      showingTenant: new TenantDetails(),
      tenantList: new TenantList(),
      modulesOfLicense: [],
      alertMessage: ''
    });
    this.accessStore = new AccessStore();
  }

  /**
   * inititial for role setup store
   * @method init
   */
  TenantManagementStore.prototype.init = function() {
    // whenever uiState changed the tenant.
    uiState.setCheckDirtyFunction(
      function() {
        return this.showingTenantCloned.hasDirty;
      }.bind(this)
    );
  };

  /**
   * destroy observe
   * @method destroy
   */
  TenantManagementStore.prototype.destroy = function() {
    uiState.resetCheckDirtyFunction();
  };

  /**
   * Get Initial Data
   */
  TenantManagementStore.prototype.getTenantList = function(isSelectFirstTenant) {
    this.resetAlertMessage();

    var tenantList = m.action(
      function(result) {
        result = result || [];
        this.tenantList.populateData(result);

        if (result.length > 0) {
          if (!isBoolean(isSelectFirstTenant) && isSelectFirstTenant) {
            var tenant = this.tenantList.getFilterList()[0];
            this.loadTenant(tenant);
          }
        } else {
          this.addTenant();
        }

        return result;
      }.bind(this)
    );

    return fetcher.getAllBasicTenantList().then(tenantList);
  };

  /**
   * Get modules of License
   */
  TenantManagementStore.prototype.getModulesOfLicense = function() {
    return fetcher.getAllModulesOfLicense().then(
      function(result) {
        this.modulesOfLicense = result || [];
        return result;
      }.bind(this)
    );
  };

  /**
   * Set showing tenant
   */
  TenantManagementStore.prototype.setShowingTenant = m.action(function(tenant) {
    // reset data of previous tenant
    this.showingTenant && this.showingTenant.setSelected(false);

    // update new selected tenant
    tenant && (this.showingTenant = tenant);
    this.showingTenant.setSelected(true);
  });

  /**
   * set showing tenant by index
   * @param {number} index
   */
  TenantManagementStore.prototype.setShowingTenantByIndex = function(index) {
    var list = this.tenantList.tenantList;
    this.loadTenant(list[index]);
  };

  /**
   * Add tenant
   */
  TenantManagementStore.prototype.addTenant = m.action(function() {
    this.showingTenant.setSelected(false);
    this.showingTenantCloned.setSelected(false);

    // Extract all modules's name and add it to a new Tenant
    var tenantModules = map(
      function(modules) {
        return modules.name;
      }.bind(this),
      this.modulesOfLicense
    );

    this.showingTenantCloned = new TenantDetails({ tenantModules: tenantModules });

    this.permissionStore = new PermissionStore(this.showingTenantCloned.permission, null, IS_TENANT_SETUP);
    this.accessStore.updateModel(this.showingTenantCloned.permissionAccessModel);
    this.accessStore.initialAccessGrid();

    this.resetAlertMessage();
  });

  /**
   * Update Filter Text
   * @param newValue
   */
  TenantManagementStore.prototype.updateFilterText = function(newValue) {
    this.tenantList.filterText = newValue;
    if (this.tenantList.filterTenants.length > 0) {
      this.setShowingTenant(this.tenantList.filterTenants[0]);
    }
  };

  /**
   * Save tenant
   * @param tenant
   * @returns {*|Promise.<T>}
   */
  TenantManagementStore.prototype.saveTenant = function() {
    function setAlertMessage(response) {
      this.alertMessage = {
        type: response.success ? 'success' : 'error',
        message: response.success
          ? localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
          : response.messages[0].messages || null
      };
      return response.messages;
    }

    var savedTenant = this.showingTenantCloned;

    // //Get update access
    var updatedAccess = this.showingTenantCloned.permissionAccessModel.getAccessForSave();
    // //Update list access default
    savedTenant.permission.updateAccessDefault(updatedAccess);

    return fetcher
      .saveTenant(savedTenant)
      .then(
        function(response) {
          if (response.success) {
            if (savedTenant.isNew) {
              this.getTenantList(false).then(
                function() {
                  var tenantDetail = find({ id: response.tenant.id }, this.tenantList.tenantList);
                  tenantDetail && this.loadTenant(tenantDetail);
                }.bind(this)
              );
            } else {
              this.showingTenant.populateData(response.tenant);
              this.loadTenant(this.showingTenant);
            }

            this.showingTenantCloned.cleanDirty();
            this.showingTenant.updateVersion();
            this.accessStore.updateAccessOrigin(this.showingTenantCloned.permission.access.accessDefaults.value);
          }
          return response;
        }.bind(this)
      )
      .then(setAlertMessage.bind(this));
  };

  /**
   * Delete tenant
   * @param id
   * @returns {*|Promise.<T>}
   */
  TenantManagementStore.prototype.deleteTenant = m.action(function(id) {
    var showingTenantIndex = findIndex(function(tenant) {
      return tenant.id === id;
    }, this.tenantList.tenantList);

    var setTenant = m.action(function() {
      var list = this.tenantList.tenantList;
      if (list.length > 0) {
        if (showingTenantIndex >= list.length) {
          showingTenantIndex = list.length - 1;
        }
        this.setShowingTenantByIndex(showingTenantIndex);
      }
    });

    return fetcher.deleteTenant(id).then(
      function(response) {
        if (response.success) {
          this.getTenantList()
            .bind(this)
            .then(setTenant);
          return Promise.resolve(true);
        }
        return response;
      }.bind(this)
    );
  });

  /**
   * Active Tenant
   * @param id
   * @returns {Promise.<T>}
   */
  TenantManagementStore.prototype.activeTenant = function(tenant) {
    tenant.setActive(true);
    this.showingTenant === tenant && this.setTenantStatus(tenant, true);
    return fetcher.activeTenant(tenant.id);
  };

  /**
   * set status of the virtunal tenant detail
   * @param  {object} tenant
   * @param  {boolean} state)
   */
  TenantManagementStore.prototype.setTenantStatus = m.action(function(tenant, state) {
    tenant && (this.showingTenant = tenant);
    this.showingTenant.setActive(state);
    this.cloneTenantDetail();
  });

  /**
   * Deactive Tenant
   * @param id
   * @returns {Promise.<T>}
   */
  TenantManagementStore.prototype.deactiveTenant = m.action(function(tenant) {
    tenant.setActive(false);
    this.showingTenant === tenant && this.setTenantStatus(tenant, false);
    return fetcher.deactiveTenant(tenant.id);
  });

  /**
   * Get Bundle
   */
  TenantManagementStore.prototype.getBundle = function() {
    return constants;
  };

  /**
   * Set Alert message
   */
  TenantManagementStore.prototype.setAlertMessage = function(type, message) {
    this.alertMessage = {
      type: type,
      message: message
    };
  };

  /**
   * Reset Alert message
   */
  TenantManagementStore.prototype.resetAlertMessage = function() {
    this.alertMessage = CommonMessages.EMPTY_MESSAGE;
  };

  /**
   * clone to new virtual TenantDetail
   * @return {Object} [this.showingTenantCloned]
   */
  TenantManagementStore.prototype.cloneTenantDetail = function() {
    this.showingTenantCloned = new TenantDetails(cloneDeep(MobxUtils.toJS(this.showingTenant)));
  };

  /**
   * Load tenant detail
   * @return {Object} Promise
   */
  TenantManagementStore.prototype.loadTenant = m.action(function(tenant) {
    var tenantDetail = m.action(function(result) {
      tenant.populateData(result);
      this.setShowingTenant(tenant);
      this.cloneTenantDetail();
      this.permissionStore = new PermissionStore(this.showingTenantCloned.permission, tenant.id, IS_TENANT_SETUP);
      this.accessStore.updateModel(this.showingTenantCloned.permissionAccessModel);
      this.accessStore.initialAccessGrid(result.permission.access.accessDefaults.value);
      return Promise.resolve();
    });

    return fetcher
      .loadTenant(tenant.id)
      .bind(this)
      .then(tenantDetail);
  });

  /**
   * Validation list accesses setting
   * @return {object} Promise result
   */
  TenantManagementStore.prototype.validateAccesses = function() {
    var updatedAccess = this.showingTenantCloned.permissionAccessModel.getAccessForSave(true);
    return accessFetcher.validateAccesses(updatedAccess);
  };

  module.exports = TenantManagementStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.TenantManagementStore.js